import { useState } from 'react'
import { Button, Input, Label, Textarea } from '@brent/core-ui/ui'
import { Loader2, Plus, Trash2 } from 'lucide-react'
import type { CreateSurveyInput, LikertQuestion } from '@/services/api/surveys'

interface SurveyFormProps {
  initialTitle?: string
  initialDescription?: string
  initialQuestions?: LikertQuestion[]
  initialFreeTextQuestion?: string
  onSubmit: (input: CreateSurveyInput) => void
  isPending: boolean
  submitLabel?: string
}

export default function SurveyForm({
  initialTitle = '',
  initialDescription = '',
  initialQuestions,
  initialFreeTextQuestion = '',
  onSubmit,
  isPending,
  submitLabel = 'Create Survey',
}: SurveyFormProps) {
  const [title, setTitle] = useState(initialTitle)
  const [description, setDescription] = useState(initialDescription)
  const [questions, setQuestions] = useState<LikertQuestion[]>(
    initialQuestions ?? [{ id: 'q1', text: '' }],
  )
  const [freeTextQuestion, setFreeTextQuestion] = useState(initialFreeTextQuestion)

  const handleAddQuestion = () => {
    const nextId = `q${questions.length + 1}`
    setQuestions([...questions, { id: nextId, text: '' }])
  }

  const handleRemoveQuestion = (index: number) => {
    if (questions.length <= 1) return
    const updated = questions
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, id: `q${i + 1}` }))
    setQuestions(updated)
  }

  const handleQuestionChange = (index: number, text: string) => {
    const updated = [...questions]
    updated[index] = { ...updated[index], text }
    setQuestions(updated)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      likertQuestions: questions,
      freeTextQuestion: freeTextQuestion.trim(),
    })
  }

  const isValid =
    title.trim().length > 0 &&
    questions.every((q) => q.text.trim().length > 0) &&
    freeTextQuestion.trim().length > 0

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="title">Survey Title</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="End of Season Survey"
          maxLength={200}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Please share your thoughts on the season."
          rows={2}
          maxLength={2000}
        />
      </div>

      <div className="space-y-3">
        <Label>Likert Questions (Strongly Disagree to Strongly Agree)</Label>
        {questions.map((question, index) => (
          <div key={question.id} className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
            <Input
              value={question.text}
              onChange={(e) => handleQuestionChange(index, e.target.value)}
              placeholder={`e.g. I enjoyed the repertoire this season`}
            />
            {questions.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => handleRemoveQuestion(index)}
                aria-label={`Remove question ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={handleAddQuestion}>
          <Plus className="mr-2 h-4 w-4" />
          Add Question
        </Button>
      </div>

      <div className="space-y-2">
        <Label htmlFor="freeTextQuestion">Free-Text Question</Label>
        <Input
          id="freeTextQuestion"
          value={freeTextQuestion}
          onChange={(e) => setFreeTextQuestion(e.target.value)}
          placeholder="Any other comments?"
          maxLength={2000}
        />
      </div>

      <Button type="submit" disabled={!isValid || isPending}>
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {submitLabel}
      </Button>
    </form>
  )
}
